import React, { useState, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { PRIVACY_POLICY, TERMS_OF_SERVICE } from '../../data/paperCompanyInfo';
import { useSelector, useDispatch } from 'react-redux';
import {
  Users,
  Menu,
  X,
  Search,
  Music2,
  Disc,
  ListVideo,
  Film,
  TrendingUp,
  UserCog,
  LogOut,
  LogIn,
  Shield,
  ShieldCheck,
  Headphones,
  Radio,
  BookOpen,
  Send,
  Mail,
  MessageSquare,
} from 'lucide-react';
import axios from 'axios';
import { authAction, authSelector } from '../../features/auth/slice';
import useAuthInit from '../../hooks/useAuthInit';
import { PUBLIC_BASE } from '../../pages/public/config';
import UserAvatar from '../UserAvatar';
import { useMessage } from '../../contexts/MessageContext';
import { useMusicPlayer } from '../../contexts/MusicPlayerContext';

const LOGO_URL =
  'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/logo.png';
const FALLBACK_IMG =
  'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/none_image.jpg';

function PublicLayout({ children, bgClassName }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { me } = useSelector(authSelector.selectAuthState);
  const { loading: authLoading } = useAuthInit();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const { lyricsBarVisible } = useMusicPlayer();
  const { unreadCount, openInbox } = useMessage() || {};

  const logout = () => {
    dispatch(authAction.logout());
    localStorage.removeItem('token');
    navigate(`${PUBLIC_BASE || '/'}`);
  };

  // 검색 상태
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const debounceRef = useRef(null);

  const navLinks = [
    { to: `${PUBLIC_BASE}/songs`, label: '최신음악', icon: Music2, gradientId: 'nav-g0', colors: ['#38bdf8', '#818cf8'] },
    { to: `${PUBLIC_BASE}/playlists`, label: '플레이리스트', icon: ListVideo, gradientId: 'nav-g1', colors: ['#a78bfa', '#e879f9'] },
    { to: `${PUBLIC_BASE}/music-videos`, label: '뮤직비디오', icon: Film, gradientId: 'nav-g2', colors: ['#fb7185', '#f472b6'] },
    { to: `${PUBLIC_BASE}/magazine/music-trends`, label: '매거진', icon: TrendingUp, gradientId: 'nav-g3', colors: ['#34d399', '#2dd4bf'], activePrefix: `${PUBLIC_BASE}/magazine` },
    { to: `${PUBLIC_BASE}/artists`, label: '아티스트', icon: Users, gradientId: 'nav-g4', colors: ['#fbbf24', '#fb923c'] },
    { to: `${PUBLIC_BASE}/community/notice`, label: '커뮤니티', icon: MessageSquare, gradientId: 'nav-g5', colors: ['#60a5fa', '#a78bfa'], activePrefix: `${PUBLIC_BASE}/community` },
  ];

  const isActive = (to, prefixes) => {
    if (prefixes) return prefixes.some(p => pathname === p || pathname.startsWith(p + '/'));
    return pathname === to || pathname.startsWith(to + '/');
  };

  const handleSettlementClick = () => {
    if (me && me.userType === 'client') {
      navigate('/dashboard');
    } else {
      navigate(`${PUBLIC_BASE}/login?tab=client`);
    }
  };

  const doSearch = useCallback(async (q) => {
    if (!q.trim()) {
      setSearchResults(null);
      return;
    }
    setSearchLoading(true);
    try {
      const res = await axios.get('/public/search', { params: { q } });
      setSearchResults(res.data);
    } catch {
      setSearchResults({ albums: [], artists: [] });
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchValue(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!val.trim()) {
      setSearchResults(null);
      return;
    }
    debounceRef.current = setTimeout(() => doSearch(val), 350);
  };

  const clearSearch = () => {
    setSearchValue('');
    setSearchResults(null);
  };

  const handleSearchBlur = () => {
    setTimeout(() => setSearchResults(null), 200);
  };

  const handleResultClick = (type, id) => {
    clearSearch();
    if (type === 'album') navigate(`${PUBLIC_BASE}/albums/${id}`);
    else navigate(`${PUBLIC_BASE}/artists/${id}`);
  };

  const hasResults =
    searchResults &&
    (searchResults.albums?.length > 0 || searchResults.artists?.length > 0);

  const SearchDropdown = ({ dropUp = false }) =>
    searchLoading || hasResults || (searchResults && !hasResults) ? (
      <div
        className={`absolute ${dropUp ? 'bottom-full mb-1' : 'top-full mt-1'} left-0 right-0 bg-[#1a2b42] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50`}>
        {searchLoading ? (
          <div className="flex items-center justify-center gap-2 py-4 text-slate-400 text-sm">
            <div className="w-3 h-3 rounded-full border-2 border-sky-400 border-t-transparent animate-spin" />
            검색 중...
          </div>
        ) : !hasResults ? (
          <p className="text-center text-slate-500 text-sm py-4">결과 없음</p>
        ) : (
          <div>
            {searchResults.albums?.length > 0 && (
              <div className="p-2">
                <p className="text-[10px] text-slate-500 uppercase tracking-widest px-2 pb-1">
                  앨범
                </p>
                {searchResults.albums.map((album) => (
                  <button
                    key={album.id}
                    onMouseDown={() => handleResultClick('album', album.id)}
                    className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-white/5 text-left transition-colors">
                    <img
                      src={album.image || FALLBACK_IMG}
                      alt={album.nameKr}
                      className="w-8 h-8 rounded object-cover shrink-0"
                    />
                    <div className="min-w-0">
                      <p className="text-white text-xs font-medium truncate">
                        {album.nameKr}
                      </p>
                      <p className="text-slate-500 text-[11px] truncate">
                        {album.artist?.nameKr}
                      </p>
                    </div>
                    <Disc
                      size={13}
                      className="text-slate-600 shrink-0 ml-auto"
                    />
                  </button>
                ))}
              </div>
            )}
            {searchResults.artists?.length > 0 && (
              <div className="p-2 border-t border-white/5">
                <p className="text-[10px] text-slate-500 uppercase tracking-widest px-2 pb-1">
                  아티스트
                </p>
                {searchResults.artists.map((artist) => (
                  <button
                    key={artist.id}
                    onMouseDown={() => handleResultClick('artist', artist.id)}
                    className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-white/5 text-left transition-colors">
                    <img
                      src={artist.image || FALLBACK_IMG}
                      alt={artist.nameKr}
                      className="w-8 h-8 rounded-full object-cover shrink-0"
                    />
                    <p className="text-white text-xs font-medium truncate">
                      {artist.nameKr}
                    </p>
                    <Users
                      size={13}
                      className="text-slate-600 shrink-0 ml-auto"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    ) : null;

  return (
    <div className={`min-h-dvh text-white flex flex-col ${bgClassName ?? 'bg-[#0d1526]'}`}>
      {/* SVG 그라데이션 정의 (메뉴 아이콘용) */}
      <svg width="0" height="0" className="absolute">
        <defs>
          {navLinks.map(({ gradientId, colors }) => (
            <linearGradient key={gradientId} id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={colors[0]} />
              <stop offset="100%" stopColor={colors[1]} />
            </linearGradient>
          ))}
          <linearGradient id="nav-g6" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ef4444" />
            <stop offset="100%" stopColor="#f97316" />
          </linearGradient>
          <linearGradient id="nav-g7" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#a78bfa" />
            <stop offset="100%" stopColor="#f472b6" />
          </linearGradient>
        </defs>
      </svg>
      <header className="sticky top-0 z-40 bg-[#0d1526]/95 backdrop-blur-xl">
        {/* 1행: 로고 · 검색 · 유저 정보 */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="h-[52px] flex items-center gap-5">
            {/* 로고 */}
            <Link to={PUBLIC_BASE || '/'} className="shrink-0">
              <img src={LOGO_URL} alt="impink" className="h-7 w-auto" />
            </Link>

            {/* 검색바 */}
            <div className="flex-1 relative max-w-sm">
              <Search
                size={15}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none"
              />
              <input
                type="text"
                value={searchValue}
                onChange={handleSearchChange}
                onBlur={handleSearchBlur}
                placeholder="앨범, 아티스트 검색"
                className="w-full pl-9 pr-8 py-[7px] bg-white/[0.05] border border-white/[0.08] rounded-full text-[15px] text-white placeholder-slate-500 focus:outline-none focus:bg-white/[0.08] focus:border-sky-500/30 transition-all"
              />
              {searchValue && (
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
                  <X size={14} />
                </button>
              )}
              <SearchDropdown />
            </div>

            {/* 우측: 로그인/유저 정보 */}
            <div className="hidden lg:flex items-center gap-2 ml-auto shrink-0">
              {authLoading ? (
                <div className="w-2 h-2 rounded-full bg-slate-500 animate-pulse" />
              ) : me ? (
                <div className="flex items-center gap-2">
                  <UserAvatar
                    name={me.name}
                    profileImage={me.profileImage}
                    userType={me.userType}
                    adminYn={me.adminYn}
                    size={28}
                  />
                  <span className="text-sm font-medium text-white max-w-[100px] truncate">
                    {me.name}
                  </span>
                  <button
                    title="메시지"
                    className="relative p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                    onClick={() => openInbox?.()}>
                    <Mail size={15} className={unreadCount > 0 ? 'text-sky-400' : ''} />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold animate-pulse">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>
                  {me.adminYn === 'Y' && (
                    <button
                      title="관리자"
                      className="p-1.5 rounded-md text-amber-400 hover:text-amber-300 hover:bg-amber-400/10 transition-colors"
                      onClick={() => navigate(`${PUBLIC_BASE}/admin/members`)}>
                      <Shield size={15} />
                    </button>
                  )}
                  <button
                    title="프로필 수정"
                    className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                    onClick={() => navigate(me?.userType === 'member' ? `${PUBLIC_BASE}/mypage` : '/EditProfile')}>
                    <UserCog size={15} />
                  </button>
                  <button
                    title="로그아웃"
                    className="p-1.5 rounded-md text-slate-400 hover:text-rose-400 hover:bg-rose-400/10 transition-colors"
                    onClick={logout}>
                    <LogOut size={15} />
                  </button>
                </div>
              ) : (
                <button
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-sky-600 hover:bg-sky-500 rounded-lg transition-colors text-sm font-medium text-white"
                  onClick={() => navigate(`${PUBLIC_BASE}/login`)}>
                  <LogIn size={14} />
                  로그인
                </button>
              )}
            </div>

            {/* 모바일 햄버거 */}
            <button
              className="lg:hidden ml-auto p-2 text-slate-400 hover:text-white shrink-0"
              onClick={() => setMobileOpen((v) => !v)}>
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* 행 구분선 */}
        <div className="border-b border-white/[0.05]" />

        {/* 2행: 데스크탑 내비게이션 + 사이트 전환 버튼 */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <nav className="hidden lg:flex items-center -ml-3 -mb-px nav-anim">
            {navLinks.map(({ to, label, icon: Icon, gradientId, activePrefix, activePrefixes }) => (
              <Link
                key={to}
                to={to}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-[15px] font-semibold transition-colors border-b-2 ${
                  isActive(activePrefix || to, activePrefixes)
                    ? 'text-white border-sky-400'
                    : 'text-slate-400 border-transparent hover:text-slate-200'
                }`}>
                <Icon size={14} style={{ stroke: `url(#${gradientId})` }} />
                {label}
              </Link>
            ))}

            {/* 우측: 방송심의 + 유통문의 + 사이트 전환 버튼 */}
            <div className="flex items-center gap-0 ml-auto">
              <Link
                to={`${PUBLIC_BASE}/review`}
                className={`flex items-center gap-1.5 px-2 py-2.5 text-[15px] font-semibold transition-colors border-b-2 ${
                  isActive(`${PUBLIC_BASE}/review`)
                    ? 'text-white border-sky-400'
                    : 'text-slate-400 border-transparent hover:text-slate-200'
                }`}>
                <Radio size={14} style={{ stroke: 'url(#nav-g6)' }} />
                방송심의
              </Link>
              <Link
                to={`${PUBLIC_BASE}/inquiry`}
                className={`flex items-center gap-1.5 px-2 py-2.5 text-[15px] font-semibold transition-colors border-b-2 ${
                  isActive(`${PUBLIC_BASE}/inquiry`, [`${PUBLIC_BASE}/inquiry`, `${PUBLIC_BASE}/proposal`])
                    ? 'text-white border-sky-400'
                    : 'text-slate-400 border-transparent hover:text-slate-200'
                }`}>
                <BookOpen size={14} style={{ stroke: 'url(#nav-g7)' }} />
                유통문의
              </Link>
              <div className="w-px h-4 bg-white/10 ml-2" />
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-sky-500/25 text-sky-300 ml-2">
                <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-pulse" />
                홈페이지
              </span>
              <button
                onClick={handleSettlementClick}
                className="relative px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-500/15 text-rose-400 hover:bg-rose-500/30 transition-colors ml-2">
                <div className="absolute bottom-full left-0 right-0 flex flex-col items-center animate-tooltip-bounce pointer-events-none" style={{ marginBottom: '-3px' }}>
                  <div className="backdrop-blur-sm text-[10px] leading-none font-medium whitespace-nowrap rounded-full" style={{ padding: '2px 3px', backgroundColor: 'transparent', border: '1px solid rgba(255,135,160,0.6)', color: 'rgba(255,135,160,0.85)' }}>
                    유통회원전용
                  </div>
                </div>
                정산시스템
              </button>
            </div>
          </nav>
        </div>

        {/* 2행: 모바일 네비게이션 (가로 스크롤) */}
        <div className="lg:hidden overflow-x-auto overflow-y-hidden scrollbar-hide">
          <div className="max-w-6xl mx-auto px-4">
            <nav className="flex items-center">
              {navLinks.map(({ to, label, icon: Icon, gradientId, activePrefix, activePrefixes }) => (
                <Link
                  key={to}
                  to={to}
                  className={`flex items-center gap-1.5 px-3 py-2 text-[15px] font-semibold whitespace-nowrap transition-colors border-b-2 ${
                    isActive(activePrefix || to, activePrefixes)
                      ? 'text-white border-sky-400'
                      : 'text-slate-400 border-transparent hover:text-slate-200'
                  }`}>
                  <Icon size={14} style={{ stroke: `url(#${gradientId})` }} />
                  {label}
                </Link>
              ))}
            </nav>
          </div>
        </div>

        {/* 하단 구분선 */}
        <div className="border-b border-white/[0.06]" />

        {/* 모바일 드롭다운 */}
        {mobileOpen && (
          <nav className="lg:hidden border-t border-white/10 px-4 py-3 flex flex-col gap-1 bg-[#0d1526] backdrop-blur">
            {/* 모바일 유저 영역 */}
            {me ? (
              <div className="flex items-center justify-between px-3 py-3 mb-2 border-b border-white/5">
                <div className="flex items-center gap-2 min-w-0">
                  <UserAvatar
                    name={me.name}
                    profileImage={me.profileImage}
                    userType={me.userType}
                    adminYn={me.adminYn}
                    size={28}
                  />
                  <span className="text-sm font-medium text-white truncate">{me.name}</span>
                </div>
                <div className="flex items-center gap-1 shrink-0 ml-2">
                  <button
                    className="relative p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                    onClick={() => { openInbox?.(); setMobileOpen(false); }}>
                    <Mail size={15} className={unreadCount > 0 ? 'text-sky-400' : ''} />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold animate-pulse">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>
                  {me.adminYn === 'Y' && (
                    <button
                      className="p-1.5 rounded-md text-amber-400 hover:text-amber-300 hover:bg-amber-400/10 transition-colors"
                      onClick={() => { navigate(`${PUBLIC_BASE}/admin/members`); setMobileOpen(false); }}>
                      <Shield size={15} />
                    </button>
                  )}
                  <button
                    className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                    onClick={() => { navigate(me?.userType === 'member' ? `${PUBLIC_BASE}/mypage` : '/EditProfile'); setMobileOpen(false); }}>
                    <UserCog size={15} />
                  </button>
                  <button
                    className="p-1.5 rounded-md text-slate-400 hover:text-rose-400 hover:bg-rose-400/10 transition-colors"
                    onClick={() => { logout(); setMobileOpen(false); }}>
                    <LogOut size={15} />
                  </button>
                </div>
              </div>
            ) : (
              <div className="px-3 py-3 mb-2 border-b border-white/5">
                <button
                  className="flex items-center gap-2 px-3 py-1.5 bg-sky-600 hover:bg-sky-500 rounded-lg transition-colors text-sm font-medium text-white"
                  onClick={() => { navigate(`${PUBLIC_BASE}/login`); setMobileOpen(false); }}>
                  <LogIn size={14} />
                  로그인
                </button>
              </div>
            )}

            {/* 모바일 사이트 전환 버튼 */}
            <div className="flex items-center gap-2 px-3 pt-3 mt-2 border-t border-white/5">
              <span className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-sky-500/25 text-sky-300">
                <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-pulse" />
                홈페이지
              </span>
              <button
                onClick={() => { handleSettlementClick(); setMobileOpen(false); }}
                className="flex-1 text-center px-3 py-2 rounded-lg text-xs font-semibold bg-rose-500/15 text-rose-400 hover:bg-rose-500/30 transition-colors">
                정산시스템
              </button>
            </div>
          </nav>
        )}
      </header>

      {/* 모바일 드롭다운 백드롭 */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-black/30"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* 이용약관 모달 */}
      {termsOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
          onClick={() => setTermsOpen(false)}>
          <div
            className="relative bg-[#0d1526] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <h2 className="text-white font-semibold text-lg">이용약관</h2>
              <button
                onClick={() => setTermsOpen(false)}
                className="text-slate-400 hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>
            <div className="overflow-y-auto px-6 py-5">
              <pre className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap font-sans">
                {TERMS_OF_SERVICE}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* 개인정보처리방침 모달 */}
      {privacyOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
          onClick={() => setPrivacyOpen(false)}>
          <div
            className="relative bg-[#0d1526] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <h2 className="text-white font-semibold text-lg">개인정보처리방침</h2>
              <button
                onClick={() => setPrivacyOpen(false)}
                className="text-slate-400 hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>
            <div className="overflow-y-auto px-6 py-5">
              <pre className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap font-sans">
                {PRIVACY_POLICY}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* 페이지 콘텐츠 */}
      <main key={pathname} className="flex-1 page-enter">{children}</main>

      {/* 푸터 */}
      <footer className={`relative z-10 border-t border-white/5 bg-[#0a101e] ${lyricsBarVisible ? 'pb-[136px] sm:pb-[104px]' : 'pb-[100px] sm:pb-[70px]'}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <img
                src={LOGO_URL}
                alt="impink"
                className="h-6 w-auto mb-3 opacity-70"
              />
              <p className="text-slate-500 text-xs leading-relaxed">
                상호: 핑크페이퍼 · 대표: 김태연 외 1명
                <br />
                사업자등록번호: 688-07-02108
                <br />
                경기도 남양주시 다산중앙로19번길 21 블루웨일 지식산업센터 1차 F동 307호
              </p>
            </div>
            <div className="text-slate-600 text-xs text-left sm:text-right leading-relaxed">
              <p className="text-sm font-bold tracking-wide">TEL: 1844-2549</p>
              <p>EMAIL: pink@impink.co.kr</p>
              <div className="mt-3 flex items-center gap-2 sm:justify-end">
                <span className="text-slate-600 text-[11px]">패밀리사이트</span>
                <a
                  href="https://pinkpaper.kr"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold bg-sky-500/15 text-sky-400 hover:bg-sky-500/30 transition-colors"
                >
                  핑크페이퍼
                </a>
                <a
                  href="https://kccm.app"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold bg-sky-500/15 text-sky-400 hover:bg-sky-500/30 transition-colors"
                >
                  KCCM
                </a>
              </div>
              <p className="mt-2">
                <button
                  onClick={() => setTermsOpen(true)}
                  className="hover:text-slate-400 transition-colors mr-3">
                  이용약관
                </button>
                <button
                  onClick={() => setPrivacyOpen(true)}
                  className="hover:text-slate-400 transition-colors mr-3">
                  개인정보처리방침
                </button>
                © 2023 pinkpaper. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default PublicLayout;
